// Bold Steps Canvas - Main Component
// Visualizes the "5 Bold Steps" vision canvas using React Flow
// Methodology: 1 Big Vision + 5 Bold Steps to get there

import { useState, useEffect, useCallback } from 'react';
import {
    ReactFlow,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    type Connection,
    type Node,
    type Edge,
    MarkerType,
    Position,
    type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Wand2, Trash2, Loader2, HelpCircle, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRoadmap } from '../shared/RoadmapContext';
import { canvasApi } from '../shared/api';
import { CANVAS_NODE_STYLES } from '../shared/constants';
import type { CanvasNode, CanvasEdge, CanvasNodeType } from '../shared/types';
import { toast } from 'sonner';
import { LoadingState } from '@/components/ds';

export function BoldStepsCanvas() {
    const { user } = useAuth();
    const { roadmap } = useRoadmap();
    const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
    const [loading, setLoading] = useState(true);

    // Dialog state
    const [isAddingNode, setIsAddingNode] = useState(false);
    const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
    // M4 (audit): methodology overlay starts collapsed so it doesn't cover canvas nodes.
    const [isMethodologyOpen, setIsMethodologyOpen] = useState(false);
    const [newNodeData, setNewNodeData] = useState<{ type: CanvasNodeType; content: string }>({
        type: 'step',
        content: '',
    });

    // Node editing state
    const [editingNode, setEditingNode] = useState<{ id: string; content: string } | null>(null);

    const [wizardData, setWizardData] = useState({
        vision: '',
        step5: 'Scale & Sustain',
        step4: 'Scale & Optimize',
        step3: 'Market Expansion',
        step2: 'MVP Launch',
        step1: 'Proof of Concept',
        theme: 'Strategy',
        support: 'Tech Team',
        challenge: 'Competition',
    });

    // Allowed types
    const ALLOWED_TYPES: CanvasNodeType[] = ['vision', 'step', 'theme', 'support', 'challenge', 'note'];

    // Build a React Flow node from a DB canvas node
    const toFlowNode = (n: CanvasNode): Node => ({
        id: n.id,
        type: 'default',
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        position: { x: n.position_x, y: n.position_y },
        data: { label: n.content, nodeType: n.type },
        style: {
            background: CANVAS_NODE_STYLES[n.type]?.bg || '#fff',
            borderColor: CANVAS_NODE_STYLES[n.type]?.border || '#000',
            borderWidth: 2,
            width: n.width || 200,
            padding: 10,
            borderRadius: 8,
            boxShadow: n.type === 'vision' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
            fontSize: n.type === 'vision' ? '1.1rem' : '1rem',
            fontWeight: n.type === 'vision' ? 'bold' : 'normal',
        },
    });

    // Load data
    const loadData = useCallback(async () => {
        if (!user || !roadmap) return;
        setLoading(true);
        try {
            const [nodesRes, edgesRes] = await Promise.all([
                canvasApi.listNodes(roadmap.id),
                canvasApi.listEdges(roadmap.id)
            ]);

            if (nodesRes.data) {
                setNodes((nodesRes.data as CanvasNode[]).map(toFlowNode));
            }

            if (edgesRes.data) {
                const flowEdges: Edge[] = (edgesRes.data as CanvasEdge[]).map(e => ({
                    id: e.id,
                    source: e.source_id,
                    target: e.target_id,
                    markerEnd: { type: MarkerType.ArrowClosed },
                    type: 'smoothstep',
                }));
                setEdges(flowEdges);
            }

        } catch (error: unknown) {
            if (error instanceof Error && (error.message?.includes('abort') || error.name === 'AbortError')) {
                return;
            }
            console.error('Failed to load canvas:', error);
            toast.error('Failed to load canvas');
        } finally {
            setLoading(false);
        }
    }, [user, roadmap, setNodes, setEdges]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);

    // Auto-fit view when nodes are loaded
    useEffect(() => {
        if (!loading && rfInstance && nodes.length > 0) {
            window.requestAnimationFrame(() => {
                rfInstance.fitView({ padding: 0.2 });
            });
        }
    }, [loading, rfInstance, nodes.length]);

    // Save node position on drag end
    const onNodeDragStop = async (_: unknown, node: Node) => {
        await canvasApi.updateNode(node.id, {
            position_x: node.position.x,
            position_y: node.position.y,
        });
    };

    // Open edit dialog on double-click
    const onNodeDoubleClick = useCallback((_: unknown, node: Node) => {
        setEditingNode({ id: node.id, content: String(node.data.label ?? '') });
    }, []);

    // Save edited node content
    const handleSaveNodeEdit = async () => {
        if (!editingNode || !editingNode.content.trim()) return;
        const { error } = await canvasApi.updateNode(editingNode.id, { content: editingNode.content.trim() });
        if (error) {
            toast.error('Failed to update node');
            return;
        }
        // Update local label so the canvas reflects immediately
        setNodes(nds => nds.map(n =>
            n.id === editingNode.id
                ? { ...n, data: { ...n.data, label: editingNode.content.trim() } }
                : n
        ));
        toast.success('Node updated');
        setEditingNode(null);
    };

    // Add new node
    const handleAddNode = async () => {
        if (!newNodeData.content.trim()) return;
        if (!user || !roadmap) return;

        let defaultX = 100;
        let defaultY = 300;
        if (newNodeData.type === 'vision') { defaultX = 1000; defaultY = 250; }
        else if (newNodeData.type === 'theme') { defaultX = 100; defaultY = 100; }
        else if (newNodeData.type === 'support') { defaultX = 100; defaultY = 500; }
        else if (newNodeData.type === 'challenge') { defaultX = 500; defaultY = 500; }
        defaultX += Math.random() * 50;
        defaultY += Math.random() * 50;

        const { data, error } = await canvasApi.createNode({
            owner_id: user.id,
            roadmap_id: roadmap.id,
            type: newNodeData.type,
            content: newNodeData.content,
            position_x: defaultX,
            position_y: defaultY,
        });

        if (error) {
            toast.error('Failed to create node');
            return;
        }

        if (data) {
            setNodes((nds) => [...nds, toFlowNode(data as CanvasNode)]);
            toast.success('Node added');
        }

        setNewNodeData({ type: 'step', content: '' });
        setIsAddingNode(false);
    };

    // Clear all nodes and edges
    const handleClearCanvas = async () => {
        if (!user || !roadmap) return;
        const { error } = await canvasApi.clearCanvas(roadmap.id);
        if (error) {
            toast.error('Failed to clear canvas');
        } else {
            toast.success('Canvas cleared');
            setNodes([]);
            setEdges([]);
        }
        setIsClearConfirmOpen(false);
    };

    const handleSmartGenerate = async () => {
        if (!user || !roadmap) return;
        if (!wizardData.vision.trim()) {
            toast.error("Please enter a Vision");
            return;
        }

        // BSC-4: Validate all Bold Steps are filled in
        const emptyStep = (['step1', 'step2', 'step3', 'step4', 'step5'] as const).find(k => !wizardData[k].trim());
        if (emptyStep) {
            toast.error('Please fill in all 5 Bold Steps');
            return;
        }

        setIsGenerating(true);

        try {
            const nodesToCreate = [
                { type: 'step', content: `Step 1: ${wizardData.step1}`, x: 0, y: 300, key: 's1' },
                { type: 'step', content: `Step 2: ${wizardData.step2}`, x: 260, y: 300, key: 's2' },
                { type: 'step', content: `Step 3: ${wizardData.step3}`, x: 520, y: 300, key: 's3' },
                { type: 'step', content: `Step 4: ${wizardData.step4}`, x: 780, y: 300, key: 's4' },
                { type: 'step', content: `Step 5: ${wizardData.step5}`, x: 1040, y: 300, key: 's5' },
                { type: 'vision', content: `VISION: ${wizardData.vision}`, x: 1400, y: 200, key: 'vis' },
                { type: 'theme', content: `Theme: ${wizardData.theme}`, x: 130, y: 100, key: 'thm' },
                { type: 'support', content: `Support: ${wizardData.support}`, x: 130, y: 500, key: 'sup' },
                { type: 'challenge', content: `Challenge: ${wizardData.challenge}`, x: 650, y: 500, key: 'cha' },
            ] as const;

            const createdNodesMap: Record<string, string> = {};

            for (const n of nodesToCreate) {
                const { data } = await canvasApi.createNode({
                    owner_id: user.id,
                    roadmap_id: roadmap.id,
                    type: n.type as CanvasNodeType,
                    content: n.content,
                    position_x: n.x,
                    position_y: n.y,
                });
                if (data) createdNodesMap[n.key] = data.id;
            }

            const createEdge = async (source: string, target: string) =>
                canvasApi.createEdge({ owner_id: user.id, roadmap_id: roadmap.id, source_id: source, target_id: target });

            if (createdNodesMap['s1'] && createdNodesMap['s2']) await createEdge(createdNodesMap['s1'], createdNodesMap['s2']);
            if (createdNodesMap['s2'] && createdNodesMap['s3']) await createEdge(createdNodesMap['s2'], createdNodesMap['s3']);
            if (createdNodesMap['s3'] && createdNodesMap['s4']) await createEdge(createdNodesMap['s3'], createdNodesMap['s4']);
            if (createdNodesMap['s4'] && createdNodesMap['s5']) await createEdge(createdNodesMap['s4'], createdNodesMap['s5']);
            if (createdNodesMap['s5'] && createdNodesMap['vis']) await createEdge(createdNodesMap['s5'], createdNodesMap['vis']);

            toast.success('Strategy board generated');
            setIsGeneratorOpen(false);
            // BSC-5: Reset wizard so it's ready for next use
            setWizardData({ vision: '', step5: '', step4: '', step3: '', step2: '', step1: '', theme: '', support: '', challenge: '' });
            loadData();

        } catch (e) {
            console.error(e);
            toast.error('Failed to generate strategy');
        } finally {
            setIsGenerating(false);
        }
    };

    // Handle connection
    const onConnect = useCallback(
        async (params: Connection) => {
            if (!user || !roadmap || !params.source || !params.target) return;
            setEdges((eds) => addEdge(params, eds));
            const { error } = await canvasApi.createEdge({
                owner_id: user.id,
                roadmap_id: roadmap.id,
                source_id: params.source,
                target_id: params.target
            });
            if (error) {
                toast.error('Failed to connect nodes');
            }
        },
        [setEdges, user, roadmap],
    );

    const onNodesDelete = async (nodesToDelete: Node[]) => {
        for (const node of nodesToDelete) {
            const { error } = await canvasApi.deleteNode(node.id);
            if (error) toast.error('Failed to delete node');
        }
    };

    const onEdgesDelete = async (edgesToDelete: Edge[]) => {
        for (const edge of edgesToDelete) {
            const { error } = await canvasApi.deleteEdge(edge.id);
            if (error) toast.error('Failed to delete connection');
        }
    };

    if (loading) {
        return <LoadingState label="Loading canvas..." />;
    }

    return (
        <div className="w-full h-full flex flex-col relative bg-slate-50">
            {/* Toolbar */}
            <div className="absolute top-4 left-4 z-10 flex gap-2 flex-wrap max-w-[calc(100%-2rem)]">
                <Button
                    variant="outline"
                    className="bg-white/80 backdrop-blur-sm shadow-sm"
                    onClick={() => rfInstance?.fitView({ padding: 0.2, duration: 800 })}
                >
                    Center View
                </Button>

                {/* Clear All — uses AlertDialog instead of window.confirm */}
                <Button
                    variant="outline"
                    className="bg-white/80 backdrop-blur-sm shadow-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={() => setIsClearConfirmOpen(true)}
                >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All
                </Button>

                {/* Wizard */}
                <Dialog open={isGeneratorOpen} onOpenChange={(open) => { if (!isGenerating) setIsGeneratorOpen(open); }}>
                    <DialogTrigger asChild>
                        <Button
                            variant="secondary"
                            className="bg-purple-100 text-purple-900 hover:bg-purple-200 border border-purple-200 shadow-sm"
                        >
                            <Wand2 className="h-4 w-4 mr-2" />
                            Wizard
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
                        <DialogHeader>
                            <DialogTitle>Bold Steps Strategy Wizard</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-2 overflow-y-auto pr-1 flex-1 min-h-0">
                            {/* Vision */}
                            <div className="space-y-2">
                                <Label>Bold Vision (3–5 Years) <span className="text-destructive">*</span></Label>
                                <Input
                                    placeholder="e.g. Market Leader by 2030"
                                    value={wizardData.vision}
                                    onChange={e => setWizardData({ ...wizardData, vision: e.target.value })}
                                    className="border-purple-200 focus:border-purple-500"
                                    disabled={isGenerating}
                                />
                            </div>

                            {/* 5 Steps — reverse engineering order */}
                            <div className="grid gap-3 pt-1">
                                <div>
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                                        The 5 Steps (Reverse Engineering)
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Work backward from your vision. Step 5 is just before you arrive.
                                    </p>
                                </div>
                                <div className="grid gap-2">
                                    {([
                                        { key: 'step5', label: 'Step 5 — Pre-Vision' },
                                        { key: 'step4', label: 'Step 4' },
                                        { key: 'step3', label: 'Step 3' },
                                        { key: 'step2', label: 'Step 2' },
                                        { key: 'step1', label: 'Step 1 — Immediate' },
                                    ] as const).map(({ key, label }) => (
                                        <div key={key}>
                                            <Label className="text-xs text-muted-foreground">{label}</Label>
                                            <Input
                                                value={wizardData[key]}
                                                onChange={e => setWizardData({ ...wizardData, [key]: e.target.value })}
                                                disabled={isGenerating}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Context nodes */}
                            <div className="grid gap-3 pt-1 border-t">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                                    Context Nodes
                                </Label>
                                <div className="grid gap-2">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">📦 Theme (strategic pillar)</Label>
                                        <Input
                                            value={wizardData.theme}
                                            onChange={e => setWizardData({ ...wizardData, theme: e.target.value })}
                                            placeholder="e.g. Strategy"
                                            disabled={isGenerating}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">💪 Support (what helps)</Label>
                                        <Input
                                            value={wizardData.support}
                                            onChange={e => setWizardData({ ...wizardData, support: e.target.value })}
                                            placeholder="e.g. Tech Team"
                                            disabled={isGenerating}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">⚠️ Challenge (what hinders)</Label>
                                        <Input
                                            value={wizardData.challenge}
                                            onChange={e => setWizardData({ ...wizardData, challenge: e.target.value })}
                                            placeholder="e.g. Competition"
                                            disabled={isGenerating}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <DialogFooter className="gap-2 shrink-0">
                            <Button
                                variant="outline"
                                onClick={() => setIsGeneratorOpen(false)}
                                disabled={isGenerating}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSmartGenerate}
                                disabled={isGenerating}
                                className="bg-purple-600 hover:bg-purple-700"
                            >
                                {isGenerating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Wand2 className="h-4 w-4 mr-2" />
                                        Generate Strategy Board
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Add Node */}
                <Dialog open={isAddingNode} onOpenChange={setIsAddingNode}>
                    <DialogTrigger asChild>
                        <Button className="shadow-md">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Node
                        </Button>
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                            <DialogTitle>Add Canvas Element</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Type</Label>
                                <Select
                                    value={newNodeData.type}
                                    onValueChange={(v) => setNewNodeData({ ...newNodeData, type: v as CanvasNodeType })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ALLOWED_TYPES.map((type) => (
                                            <SelectItem key={type} value={type}>
                                                <div className="flex items-center gap-2">
                                                    <span>{CANVAS_NODE_STYLES[type]?.icon}</span>
                                                    <span className="capitalize">{type === 'vision' ? 'Bold Vision' : type}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Content</Label>
                                <Input
                                    value={newNodeData.content}
                                    onChange={(e) => setNewNodeData({ ...newNodeData, content: e.target.value })}
                                    placeholder={newNodeData.type === 'vision' ? "e.g., Market Leader in 2030" : "e.g., Launch MVP"}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddNode()}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setIsAddingNode(false)}>Cancel</Button>
                            <Button onClick={handleAddNode} disabled={!newNodeData.content.trim()}>
                                Add to Canvas
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* M4 (audit): Methodology reference panel — collapsed by default so it
                doesn't sit on top of canvas nodes. Click the help button to expand. */}
            <div className="absolute top-4 right-4 z-10 hidden md:block">
                {!isMethodologyOpen ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsMethodologyOpen(true)}
                        aria-label="Show methodology reference"
                        className="bg-white/90 backdrop-blur shadow-sm"
                    >
                        <HelpCircle className="h-4 w-4 mr-1.5" />
                        Methodology
                    </Button>
                ) : (
                    <div className="bg-white/90 backdrop-blur p-4 rounded-lg shadow-sm border max-w-sm text-sm">
                        <div className="flex items-start justify-between mb-2 gap-2">
                            <h3 className="font-semibold">5 Bold Steps Methodology</h3>
                            <button
                                type="button"
                                onClick={() => setIsMethodologyOpen(false)}
                                aria-label="Close methodology reference"
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="space-y-2 text-muted-foreground text-xs">
                            <p><strong className="text-purple-600">1. Vision:</strong> Define your "North Star" (3–5 years out).</p>
                            <p><strong className="text-blue-600">2. Themes:</strong> Key pillars supporting the vision.</p>
                            <p><strong className="text-green-600">3. Bold Steps:</strong> 5 concrete, strategic moves to bridge the gap.</p>
                            <div className="flex gap-3">
                                <span><strong className="text-emerald-600">Supports:</strong> What helps?</span>
                                <span><strong className="text-red-500">Challenges:</strong> What hinders?</span>
                            </div>
                            <p className="pt-1 border-t text-muted-foreground/70">Double-click any node to edit. Drag between handles to connect.</p>
                        </div>
                    </div>
                )}
            </div>

            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDragStop={onNodeDragStop}
                onNodesDelete={onNodesDelete}
                onEdgesDelete={onEdgesDelete}
                onNodeDoubleClick={onNodeDoubleClick}
                onInit={(instance) => {
                    setRfInstance(instance);
                    setTimeout(() => instance.fitView({ padding: 0.2 }), 100);
                }}
                fitView
                className="w-full h-full bg-slate-50"
            >
                <Background />
                <Controls />
            </ReactFlow>

            {/* Node Edit Dialog */}
            <Dialog open={!!editingNode} onOpenChange={(open) => !open && setEditingNode(null)}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Edit Node</DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>Content</Label>
                        <Input
                            className="mt-1"
                            value={editingNode?.content ?? ''}
                            onChange={(e) => setEditingNode(prev => prev ? { ...prev, content: e.target.value } : null)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveNodeEdit()}
                            autoFocus
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setEditingNode(null)}>Cancel</Button>
                        <Button onClick={handleSaveNodeEdit} disabled={!editingNode?.content.trim()}>
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Clear All Confirmation */}
            <AlertDialog open={isClearConfirmOpen} onOpenChange={setIsClearConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Clear Entire Canvas?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete all nodes and connections. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleClearCanvas}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Clear All
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default BoldStepsCanvas;
